import { Stack } from '@mui/joy';
import { type FunctionComponent, Suspense, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  createAssetAutocompleteProps,
  type NotVerifiedAssetWithId,
  useAssetPaginatedOptions,
} from 'components/market/asset/AssetService.tsx';
import {
  createAccountIdAutocompleteOptions,
  createSubAccountIdAutocompleteOptions,
} from 'components/portfolio/account/AccountService.tsx';
import { useDrawer } from 'components/technical/Drawer/UseDrawer.tsx';
import FormStaticMultiAutocomplete from 'components/technical/form/FormStaticMultiAutocomplete.tsx';
import GFormProvider from 'components/technical/form/GFormProvider.tsx';
import GButton from 'components/technical/inputs/GButton/GButton.tsx';

import {
  type IPortfolioFiltersAssetInputQuery,
  type IPortfolioFiltersInputQuery,
  usePortfolioFiltersAssetInputSuspenseQuery,
  usePortfolioFiltersInputSuspenseQuery,
} from 'generated/graphql.tsx';
import {
  type Field,
  useShowSubAccountAssetFilter,
  useSubAccountAssetFilterFieldsForPath,
} from './UseShowPortfolioFilters.tsx';
import { usePortfolioFilters, useSyncPortfolioFilterWithCurrentData } from './UsePortfolioFilters.tsx';
import { createSubfundIdAutocompleteOptions } from '../../portfolio/fund/SubFundService.tsx';
import Loader from '../Loader/Loader.tsx';
import FormMultiAutocomplete from '../form/FormMultiAutocomplete.tsx';
import { calculateAvailableAccountSubAccountIds } from 'components/portfolio/PortfolioService.ts';
import { isEqual } from 'lodash/fp';

interface PortfolioFiltersForm {
  assets: NotVerifiedAssetWithId[];
  subFundIds: number[];
  accountIds: string[];
  subAccountIds: string[];
  assetIds: string[];
}

type FilterDrawerProps = {
  open: boolean;
  data: Partial<IPortfolioFiltersAssetInputQuery> & IPortfolioFiltersInputQuery;
};

const PortfolioFiltersDrawer: FunctionComponent<FilterDrawerProps> = ({ open, data }) => {
  const idToAsset = new Map((data.assets?.list ?? []).map((a) => [a.id, a]));
  const { portfolioFilters, setPortfolioFilters } = usePortfolioFilters();

  const methods = useForm<PortfolioFiltersForm>({
    defaultValues: {
      subFundIds: portfolioFilters.subFundIds ?? [],
      accountIds: portfolioFilters.accountIds ?? [],
      subAccountIds: portfolioFilters.subAccountIds ?? [],
      assets: (portfolioFilters.assetIds ?? []).map((id) => idToAsset.get(id)!),
    },
  });

  const subFundIds = useWatch({
    control: methods.control,
    name: 'subFundIds',
  });

  const accountIds = useWatch({
    control: methods.control,
    name: 'accountIds',
  });

  const subAccountIds = useWatch({
    control: methods.control,
    name: 'subAccountIds',
  });

  const {
    filteredSubAccountIds: availableSubAccountIds,
    subFundAvailableAccountIds,
    accountAvailableSubAccountIds,
  } = calculateAvailableAccountSubAccountIds({
    allSubFunds: data.portfolio.subFunds.list,
    allAccounts: data.portfolio.accounts,
    subFundIds,
    accountIds,
    subAccountIds,
  });

  const newAccountIds = accountIds.filter((accId) => subFundAvailableAccountIds.includes(accId));
  const newSubAccountIds = subAccountIds.filter((subAccId) => availableSubAccountIds.includes(subAccId));

  useEffect(() => {
    if (!isEqual(newAccountIds, accountIds)) {
      methods.setValue('accountIds', newAccountIds);
    }
  }, [newAccountIds, accountIds, methods]);

  useEffect(() => {
    if (!isEqual(newSubAccountIds, subAccountIds)) {
      methods.setValue('subAccountIds', newSubAccountIds);
    }
  }, [newSubAccountIds, subAccountIds, methods]);

  const fields = useSubAccountAssetFilterFieldsForPath();
  const shouldShowField = (fieldName: Field): boolean => fields?.includes(fieldName) ?? true;

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (open) {
      const idToAsset = new Map((data.assets?.list ?? []).map((a) => [a.id, a]));
      // reset from dirty values and set applied filters values on drawer reopen
      methods.setValue('subFundIds', portfolioFilters.subFundIds ?? []);
      methods.setValue('subAccountIds', portfolioFilters.subAccountIds ?? []);
      methods.setValue('accountIds', portfolioFilters.accountIds ?? []);
      methods.setValue(
        'assets',
        (portfolioFilters.assetIds ?? []).map((id) => idToAsset.get(id)!)
      );
    }
    // reset only makes sense on open drawer event
  }, [open, data]);

  const subFunds = data.portfolio.subFunds.list;
  const onSubmit = (formData: PortfolioFiltersForm): void => {
    setPortfolioFilters({
      subFundIds: formData.subFundIds,
      subAccountIds: formData.subAccountIds,
      accountIds: formData.accountIds,
      assetIds: formData.assets.map((asset) => asset.id),
    });
  };

  const assetOptions = useAssetPaginatedOptions();
  const subAccountOptionsConfig = createSubAccountIdAutocompleteOptions(data.portfolio.accounts);
  const filteredByAccSubAccountOptions = subAccountOptionsConfig.options.filter((option) =>
    accountAvailableSubAccountIds.includes(option.value)
  );

  return (
    <GFormProvider {...methods}>
      <Stack spacing={1.5}>
        {shouldShowField('subFundIds') && (
          <FormStaticMultiAutocomplete
            name="subFundIds"
            {...createSubfundIdAutocompleteOptions(subFunds)}
            label="Sub-fund"
            width="fullWidth"
          />
        )}
        {shouldShowField('accountIds') && (
          <FormStaticMultiAutocomplete
            disabled={subFundAvailableAccountIds.length === 0}
            name="accountIds"
            {...createAccountIdAutocompleteOptions(
              data.portfolio.accounts.filter((acc) => subFundAvailableAccountIds.includes(acc.id))
            )}
            label="Account"
            width="fullWidth"
          />
        )}
        {shouldShowField('subAccountIds') && (
          <FormStaticMultiAutocomplete
            disabled={filteredByAccSubAccountOptions.length === 0}
            name="subAccountIds"
            {...subAccountOptionsConfig}
            options={filteredByAccSubAccountOptions}
            label="Sub-account"
            width="fullWidth"
          />
        )}
        {shouldShowField('assets') && (
          <FormMultiAutocomplete
            {...assetOptions}
            {...createAssetAutocompleteProps<NotVerifiedAssetWithId>()}
            width="fullWidth"
            label="Asset"
            name="assets"
            limitTags={3}
          />
        )}
        <GButton
          variant="plain"
          onClick={(): void => {
            methods.reset({
              subFundIds: [],
              accountIds: [],
              subAccountIds: [],
              assets: [],
            });
          }}
        >
          Clear all
        </GButton>
        <GButton onClick={methods.handleSubmit(onSubmit)}>Apply filter</GButton>
      </Stack>
    </GFormProvider>
  );
};

const SubAccountAssetFilterDrawerContainer: FunctionComponent<{ open: boolean }> = ({ open }) => {
  const { portfolioFilters } = usePortfolioFilters();

  // run two queries - when calling endpoint with assetIds empty, the backend returns the list of all assets
  const { data } = usePortfolioFiltersInputSuspenseQuery();

  const { data: assetData } = usePortfolioFiltersAssetInputSuspenseQuery({
    variables: {
      assetIds: portfolioFilters.assetIds ?? [],
    },
    skip: (portfolioFilters.assetIds ?? []).length === 0,
  });

  const fullData = {
    ...data!,
    ...assetData,
  };

  useSyncPortfolioFilterWithCurrentData(fullData);

  return <PortfolioFiltersDrawer open={open} data={fullData} />;
};

const SubAccountAssetFilterDrawerWrapper: FunctionComponent<{ open: boolean }> = ({ open }) => {
  const { drawer, closeDrawer } = useDrawer();
  const shouldShowSubAccountAssetFilter = useShowSubAccountAssetFilter();

  useEffect(() => {
    if (drawer && !shouldShowSubAccountAssetFilter) {
      closeDrawer();
    }
  }, [drawer, closeDrawer, shouldShowSubAccountAssetFilter]);

  return (
    <Suspense fallback={<Loader />}>
      <SubAccountAssetFilterDrawerContainer open={open} />
    </Suspense>
  );
};

export default SubAccountAssetFilterDrawerWrapper;
